"use client"

import { useState, useRef, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useForm, Controller } from "react-hook-form"
import { toast } from "react-toastify"
import { updateProfile, resetAuthState } from "../../slices/authSlice"
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  TextField,
  Typography,
  Avatar,
  Grid,
  InputAdornment,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material"
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  PhotoCamera as PhotoCameraIcon,
  Save as SaveIcon,
  LocationCity as LocationCityIcon,
} from "@mui/icons-material"
import { API_URL } from "../../constants"
import axios from "axios"

// Helper function to ensure we're working with string IDs consistently
const ensureStringId = (id) => {
  if (!id) return ""
  return typeof id === "object" ? id.toString() : id
}

const ProfileForm = () => {
  const dispatch = useDispatch()
  const { userInfo, loading, error, success } = useSelector((state) => state.auth)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const [profileImageUrl, setProfileImageUrl] = useState("")
  const fileInputRef = useRef(null)

  const [stateId, setStateId] = useState("")
  const [cityId, setCityId] = useState("")
  const [states, setStates] = useState([])
  const [cities, setCities] = useState([])
  const [loadingStates, setLoadingStates] = useState(false)
  const [loadingCities, setLoadingCities] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      name: userInfo?.user?.name || "",
      email: userInfo?.user?.email || "",
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  })

  // Initialize profile image URL from user data
  useEffect(() => {
    if (userInfo?.user?.profileImage) {

      // Construct full URL based on the path
      let imageUrl = userInfo.user.profileImage

      // If it's a relative path, make it absolute
      if (imageUrl && imageUrl.startsWith("/")) {
        const baseUrl = API_URL.endsWith("/") ? API_URL.slice(0, -1) : API_URL
        imageUrl = `${baseUrl}${imageUrl}`
      } else if (imageUrl && !imageUrl.startsWith("http")) {
        // If it's not a full URL and not starting with /, add / and make it absolute
        const baseUrl = API_URL.endsWith("/") ? API_URL.slice(0, -1) : API_URL
        imageUrl = `${baseUrl}/${imageUrl}`
      }

      setProfileImageUrl(imageUrl)
    } else {
      setProfileImageUrl("")
    }
  }, [userInfo])

  // Handle success and error states
  useEffect(() => {
    if (success) {
      toast.success("Profile updated successfully")
      dispatch(resetAuthState())
      reset({
        name: userInfo?.user?.name || "",
        email: userInfo?.user?.email || "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      })
    }
  }, [success, dispatch, reset, userInfo])

  useEffect(() => {
    if (error) {
      toast.error(error)
      dispatch(resetAuthState())
    }
  }, [error, dispatch])

  // Fetch states on component mount
  useEffect(() => {
    const fetchStates = async () => {
      setLoadingStates(true)
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        }
        const { data } = await axios.get(`${API_URL}/api/locations/states`, config)
        setStates(data.states || [])
      } catch (error) {
        console.error("Failed to fetch states:", error)
      } finally {
        setLoadingStates(false)
      }
    }

    fetchStates()
  }, [userInfo.token])

  // Fetch cities when state changes
  useEffect(() => {
    if (!stateId) {
      setCities([])
      return
    }

    const fetchCities = async () => {
      setLoadingCities(true)
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        }
        const { data } = await axios.get(`${API_URL}/api/locations/states/${stateId}/cities`, config)
        setCities(data.cities || [])
      } catch (error) {
        console.error("Failed to fetch cities:", error)
      } finally {
        setLoadingCities(false)
      }
    }

    fetchCities()
  }, [stateId, userInfo.token])

  // Initialize state and city from user data if available
  useEffect(() => {
    if (userInfo?.user) {
      
      // Check if state exists in user data and states are loaded
      if (userInfo.user.state && states.length > 0) {
        const stateIdString = ensureStringId(userInfo.user.state)
        const stateExists = states.some(state => state._id === stateIdString)
        
        if (stateExists) {
          setStateId(stateIdString)
        } else {
          console.log("User state ID not found in states array")
        }
      } else {
        console.log("No state in user data or states not loaded yet")
      }
    }
  }, [userInfo, states])

  // Initialize city from user data if available and state is set
  useEffect(() => {
    if (userInfo?.user?.city && stateId && cities.length > 0) {
      const cityIdString = ensureStringId(userInfo.user.city)
      const cityExists = cities.some(city => city._id === cityIdString)
      
      if (cityExists) {
        console.log("Setting city from user data:", cityIdString)
        setCityId(cityIdString)
      } else {
        console.log("User city ID not found in cities array")
      }
    }
  }, [userInfo, cities, stateId])

  const handleImageChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    try {
      setIsUploading(true)
      setUploadError(null)

      // Create form data for the file upload
      const formData = new FormData()
      formData.append("file", file)

      console.log("Uploading to:", `${API_URL}/api/upload`)

      // Upload the file to your backend
      const response = await fetch(`${API_URL}/api/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
        body: formData,
      })

      console.log("Upload response status:", response.status)

      if (!response.ok) {
        const errorText = await response.text()
        console.error("Upload error response:", errorText)
        throw new Error(`Failed to upload image: ${response.status} ${errorText}`)
      }

      const data = await response.json()
      console.log("Upload response data:", data)

      if (data.success) {
        // Get the image path from the response
        const imagePath = data.filePath
        console.log("Image path from server:", imagePath)

        // Construct the full URL for display
        const baseUrl = API_URL.endsWith("/") ? API_URL.slice(0, -1) : API_URL
        const fullImageUrl = imagePath.startsWith("/") ? `${baseUrl}${imagePath}` : `${baseUrl}/${imagePath}`

        console.log("Full image URL to display:", fullImageUrl)

        // Update local state for immediate display
        setProfileImageUrl(fullImageUrl)

        // Update the user profile with the image path
        try {
          console.log("Updating profile with image path:", imagePath)
          // Include the old image path so the backend can delete it
          const oldImagePath = userInfo?.user?.profileImage
          console.log("Old image path:", oldImagePath)

          const updateResult = await dispatch(
            updateProfile({
              profileImage: imagePath,
              oldProfileImage: oldImagePath, // Send the old path for deletion
            }),
          ).unwrap()

          console.log("Profile update result:", updateResult)

          if (updateResult && updateResult._id) {
            console.log("Profile update successful, image path should be saved")
          }
        } catch (updateError) {
          console.error("Failed to update profile with new image:", updateError)
          toast.error("Image uploaded but failed to update profile")
        }
      } else {
        throw new Error(data.message || "Failed to upload image")
      }
    } catch (error) {
      console.error("Failed to upload image:", error)
      setUploadError(error.message)
      toast.error(`Failed to upload image: ${error.message}`)
    } finally {
      setIsUploading(false)
    }
  }

  const handleStateChange = (event) => {
    const newStateId = ensureStringId(event.target.value)
    console.log("State changed to:", newStateId)
    setStateId(newStateId)
    setCityId("") // Reset city when state changes
  }

  const handleCityChange = (event) => {
    const newCityId = ensureStringId(event.target.value)
    console.log("City changed to:", newCityId)
    setCityId(newCityId)
  }

  const onSubmit = (data) => {
    // Check if passwords match
    if (data.newPassword && data.newPassword !== data.confirmPassword) {
      toast.error("New passwords do not match")
      return
    }

    // Prepare update data
    const updateData = {
      name: data.name,
      email: data.email,
      state: stateId,
      city: cityId,
    }

    // Only include password fields if provided
    if (data.currentPassword) {
      updateData.currentPassword = data.currentPassword
    }

    if (data.newPassword) {
      updateData.newPassword = data.newPassword
    }

    // Include the profile image path if it exists
    if (userInfo?.user?.profileImage) {
      updateData.profileImage = userInfo.user.profileImage
    }

    console.log("Submitting profile update with data:", updateData)
    dispatch(updateProfile(updateData))
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Card elevation={2}>
        <CardHeader title="Personal Information" subheader="Update your personal details and account information" />
        <Divider />
        <CardContent>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 4 }}>
            <Box sx={{ position: "relative", mb: 2 }}>
              <Avatar
                src={profileImageUrl || ""}
                alt={userInfo?.user?.name || "User"}
                sx={{
                  width: 100,
                  height: 100,
                  border: 3,
                  borderColor: "primary.main",
                  fontSize: "2.5rem",
                  bgcolor: "primary.main",
                }}
                imgProps={{
                  onError: (e) => {
                    console.error("Image failed to load:", profileImageUrl)
                    e.target.onerror = null
                    e.target.src = "" // Clear the src to show the fallback
                  },
                }}
              >
                {userInfo?.user?.name?.charAt(0) || "U"}
              </Avatar>
              {isUploading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    borderRadius: "50%",
                  }}
                >
                  <CircularProgress size={40} />
                </Box>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                ref={fileInputRef}
                style={{ display: "none" }}
                disabled={isUploading}
              />
              <IconButton
                color="primary"
                aria-label="upload picture"
                component="span"
                onClick={() => fileInputRef.current.click()}
                disabled={isUploading}
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  bgcolor: "background.paper",
                  boxShadow: 1,
                  "&:hover": { bgcolor: "grey.100" },
                }}
              >
                <PhotoCameraIcon />
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Click the camera icon to change your profile photo
            </Typography>
            {uploadError && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {uploadError}
              </Typography>
            )}
          </Box>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: "Name is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Full Name"
                      variant="outlined"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Email Address"
                      variant="outlined"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="state-label">State</InputLabel>
                  <Select
                    labelId="state-label"
                    id="state"
                    value={stateId}
                    onChange={handleStateChange}
                    disabled={loading || loadingStates}
                    label="State"
                    startAdornment={
                      <InputAdornment position="start">
                        <LocationCityIcon />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="">
                      <em>Select State</em>
                    </MenuItem>
                    {states.map((state) => (
                      <MenuItem key={state._id} value={state._id}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {loadingStates && (
                    <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      <Typography variant="caption" color="text.secondary">
                        Loading states...
                      </Typography>
                    </Box>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth margin="normal" disabled={!stateId || loadingCities}>
                  <InputLabel id="city-label">City</InputLabel>
                  <Select
                    labelId="city-label"
                    id="city"
                    value={cityId}
                    onChange={handleCityChange}
                    disabled={loading || !stateId || loadingCities}
                    label="City"
                    startAdornment={
                      <InputAdornment position="start">
                        <LocationCityIcon />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="">
                      <em>Select City</em>
                    </MenuItem>
                    {cities.map((city) => (
                      <MenuItem key={city._id} value={city._id}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {loadingCities && (
                    <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      <Typography variant="caption" color="text.secondary">
                        Loading cities...
                      </Typography>
                    </Box>
                  )}
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Change Password
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Leave blank to keep your current password
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name="currentPassword"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="password"
                      label="Current Password"
                      variant="outlined"
                      error={!!errors.currentPassword}
                      helperText={errors.currentPassword?.message}
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="newPassword"
                  control={control}
                  rules={{
                    minLength: {
                      value: 6,
                      message: "Password must be at least 6 characters",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="password"
                      label="New Password"
                      variant="outlined"
                      error={!!errors.newPassword}
                      helperText={errors.newPassword?.message}
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="confirmPassword"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="password"
                      label="Confirm New Password"
                      variant="outlined"
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message}
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                sx={{
                  bgcolor: "#008069",
                  "&:hover": { bgcolor: "#128C7E" },
                  px: 3,
                }}
              >
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  )
}

export default ProfileForm
